import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { createCustomStateSetOnUrlHandler } from '@appState/helpers';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { getAll, getOne, StateDictionary } from '../helpers/store-base';
import {
  CategorizedRecordingsRequestParams,
  CategorizedRecordingsResponse,
  Recording,
  RecordingListItem,
  RecordingParams,
  RecordingsTopicsPreference,
  RecordingsTopicsPreferenceUpdate,
} from './recordings.model';
import { RecordingsService } from './recordings.service';

export const getUsableRecordingDate = (recording: RecordingListItem) => {
  const communityApprovedDate = recording.communities[0]!.approvedDateTimeUtc;
  const validFromDate = recording.validFrom;

  // this is needed because with recordings implementation, it's possible to get active articles
  // that are not yet approved in partner's community and approval date is null, but the comparison chooses it
  // and the date displayed in that case is 1970-01-01T00:00:00Z
  if (!communityApprovedDate) {
    return validFromDate;
  }

  return communityApprovedDate < recording.validFrom! ? recording.validFrom! : communityApprovedDate;
};

export const latestRecordingSortFunc = () => {
  return (a: RecordingListItem, b: RecordingListItem) => (getUsableRecordingDate(a) < getUsableRecordingDate(b) ? 1 : -1);
};

type RecordingsState = {
  recordings: RecordingListItem[];
  isRecordingsLoading: boolean;
  selectedRecording: Recording | null;
  isSelectedRecordingLoading: boolean;
  categorizedRecordings: CategorizedRecordingsResponse[];
  isCategorizedRecordingsLoading: boolean;

  topicsPreferences: RecordingsTopicsPreference[];
  isTopicsPreferencesLoading: boolean;
  isTopicsPreferencesUpdateLoading: boolean;

  viewTypeToggle: 'categorized' | 'full';
};

const initialState: RecordingsState = {
  recordings: [],
  isRecordingsLoading: false,
  selectedRecording: null,
  isSelectedRecordingLoading: false,
  categorizedRecordings: [],
  isCategorizedRecordingsLoading: false,

  topicsPreferences: [],
  isTopicsPreferencesLoading: false,
  isTopicsPreferencesUpdateLoading: false,

  viewTypeToggle: 'full',
};

const recordingsDictionary: StateDictionary<RecordingListItem> = {
  entityList: 'recordings',
  listLoading: 'isRecordingsLoading',
  selectedEntity: 'selectedRecording',
  selectedEntityLoading: 'isSelectedRecordingLoading',
  createLoading: '',
  entityId: 'id',
  sortFn: latestRecordingSortFunc(),
};

export const RecordingsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('recordings-store'),
  withMethods((store, recordingsService = inject(RecordingsService), router = inject(Router)) => {
    createCustomStateSetOnUrlHandler<RecordingsState>('/recordings', { topicsPreferences: [] })(store);
    const repositoryMethods = {
      getRecordings: getAll<RecordingListItem, RecordingParams>({
        store,
        service: recordingsService,
        dictionary: recordingsDictionary,
      }),

      getCategorized: rxMethod<CategorizedRecordingsRequestParams>(
        pipe(
          tap(() => patchState(store, { isCategorizedRecordingsLoading: true })),
          switchMap(filters =>
            recordingsService.getCategorized(filters).pipe(
              tap(categorizedRecordings =>
                patchState(store, {
                  categorizedRecordings: categorizedRecordings,
                  isCategorizedRecordingsLoading: false,
                }),
              ),
              catchError(() => {
                patchState(store, { isCategorizedRecordingsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getRecording: getOne<Recording>({
        store,
        service: recordingsService,
        dictionary: recordingsDictionary,
        onErrorCallback() {
          router.navigate(['recordings']);
          patchState(store, { selectedRecording: null });
        },
      }),

      getTopicsPreferences: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isTopicsPreferencesLoading: true })),
          switchMap(() =>
            recordingsService.getTopicsPreferences().pipe(
              tap(topicsPreferences => patchState(store, { topicsPreferences, isTopicsPreferencesLoading: false })),
              catchError(() => {
                patchState(store, { isTopicsPreferencesLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      updateTopicsPreferences: rxMethod<RecordingsTopicsPreferenceUpdate>(
        pipe(
          tap(() => patchState(store, { isTopicsPreferencesUpdateLoading: true })),
          switchMap(topicsPreferences =>
            recordingsService.updateTopicsPreferences(topicsPreferences).pipe(
              tap(topicsPreferences => {
                patchState(store, { topicsPreferences, isTopicsPreferencesUpdateLoading: false });
                repositoryMethods.getCategorized({ countPerTopic: 5 });
              }),
              catchError(() => {
                patchState(store, { isTopicsPreferencesUpdateLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      setViewTypeToggle(viewType: 'categorized' | 'full') {
        patchState(store, { viewTypeToggle: viewType });
      },

      resetListState() {
        patchState(store, {
          recordings: [],
          isRecordingsLoading: false,
        });
      },
    };
    return repositoryMethods;
  }),
);

export interface RecordingsStoreType {
  readonly recordings: Signal<RecordingListItem[]>;
  readonly isRecordingsLoading: Signal<boolean>;
  readonly selectedRecording: Signal<Recording | null>;
  readonly isSelectedRecordingLoading: Signal<boolean>;
  readonly categorizedRecordings: Signal<CategorizedRecordingsResponse[]>;
  readonly isCategorizedRecordingsLoading: Signal<boolean>;
  readonly topicsPreferences: Signal<RecordingsTopicsPreference[]>;
  readonly isTopicsPreferencesLoading: Signal<boolean>;
  readonly isTopicsPreferencesUpdateLoading: Signal<boolean>;
  readonly viewTypeToggle: Signal<'categorized' | 'full'>;

  readonly getRecordings: (params: RecordingParams) => void;
  readonly getCategorized: (params: CategorizedRecordingsRequestParams) => void;
  readonly getRecording: (recordingId: string) => void;
  readonly setViewTypeToggle: (viewType: 'categorized' | 'full') => void;
  readonly resetListState: () => void;
  readonly getTopicsPreferences: () => void;
  readonly updateTopicsPreferences: (topicsPreferences: RecordingsTopicsPreferenceUpdate) => void;
}
